import { Chip, Typography, makeStyles } from '@material-ui/core';

export type FacetOption = {
  id: string;
  label: string;
  /**
   * How many rows this option would leave, counted against what the *other*
   * facets already allow. The caller owns that arithmetic; this component only
   * prints the number.
   */
  count: number;
};

export type FacetRowProps = {
  label: string;
  options: FacetOption[];
  selected: string[];
  onToggle: (id: string) => void;
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    padding: theme.spacing(1, 0),
    // Separates stacked rows without wrapping each one in its own box.
    '& + &': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  label: {
    flex: '0 0 120px',
    fontWeight: 700,
    letterSpacing: '1.2px',
    color: theme.palette.text.secondary,
  },
  chips: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    flex: 1,
  },
  chip: {
    fontWeight: 600,
  },
  count: {
    marginLeft: theme.spacing(0.75),
    opacity: 0.7,
    fontWeight: 400,
  },
}));

/**
 * One dimension of a filter, drawn as a row of toggle chips.
 *
 * Purely presentational: every count and every filtering decision belongs to
 * the caller, because only the caller knows what the other rows have already
 * narrowed away.
 */
export const FacetRow = ({
  label,
  options,
  selected,
  onToggle,
}: FacetRowProps) => {
  const classes = useStyles();

  // An option that would filter everything away is a dead end, so it is not
  // offered. A selected one stays even at zero: clicking it is the way back out.
  const visible = options.filter(
    option => option.count > 0 || selected.includes(option.id),
  );

  if (visible.length === 0) return null;

  return (
    <div className={classes.root}>
      <Typography variant="overline" className={classes.label}>
        {label}
      </Typography>
      <div className={classes.chips}>
        {visible.map(option => (
          <Chip
            key={option.id}
            size="small"
            clickable
            className={classes.chip}
            color={selected.includes(option.id) ? 'primary' : 'default'}
            onClick={() => onToggle(option.id)}
            label={
              <>
                {option.label}
                <span className={classes.count}>{option.count}</span>
              </>
            }
          />
        ))}
      </div>
    </div>
  );
};
